import React, { forwardRef } from 'react';
import { FieldError, RegisterOptions } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { FormFieldProps } from '@defs/components';
import { mergeRefs, buildAriaDescribedBy, useFormIds } from './utils';
import { FieldWrapper } from './FieldWrapper';

// ============================================================================
// Styled Components
// ============================================================================

// FormGroup and Label are now handled by FieldWrapper

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input<{
  hasError: boolean;
  hasStartAdornment: boolean;
  hasEndAdornment: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  padding: 0.625rem 0.75rem;
  min-height: 2.5rem;
  border: 1px solid
    ${(props) => (props.hasError ? 'var(--danger-600)' : 'var(--border)')};
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--text-primary);
  background-color: var(--surface);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
  font-family: inherit;

  ${(props) =>
    props.hasStartAdornment &&
    css`
      padding-left: 3rem;
    `}

  ${(props) =>
    props.hasEndAdornment &&
    css`
      padding-right: 3rem;
    `}

  &::placeholder {
    color: var(--text-muted);
    opacity: 0.7;
    font-size: 1rem;
  }

  &:focus {
    outline: none;
    border-color: var(--brand-600);
    box-shadow: 0 0 0 2px var(--brand-50);
    background-color: var(--surface);
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: var(--brand-500);
    background-color: var(--surface-strong);
  }

  ${(props) =>
    props.disabled &&
    css`
      background-color: var(--neutral-100);
      color: var(--text-muted);
      cursor: not-allowed;
      opacity: 0.7;

      &::placeholder {
        color: var(--text-muted);
        opacity: 0.6;
      }
    `}

  ${(props) =>
    props.hasError &&
    css`
      border-color: var(--danger-600);

      &:focus {
        border-color: var(--danger-600);
        box-shadow: 0 0 0 2px rgba(198, 40, 40, 0.1);
      }
    `}

  &[aria-invalid="true"] {
    border-color: var(--danger-600);
  }

  /* Mobile touch targets - maintain padding but ensure min-height */
  @media (max-width: 767px) {
    min-height: 2.75rem; /* 44px touch target */
    padding: 0.625rem 0.75rem;
    font-size: 1rem;
    border-radius: 8px;

    ${(props) =>
      props.hasStartAdornment &&
      css`
        padding-left: 2.75rem;
      `}

    ${(props) =>
      props.hasEndAdornment &&
      css`
        padding-right: 2.75rem;
      `}

    &:focus {
      box-shadow: 0 0 0 3px var(--brand-50);
    }
  }

  /* Small mobile - increase comfort */
  @media (max-width: 479px) {
    min-height: 2.75rem;
  }

  /* Remove default styling for specific input types */
  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  /* Number input styling */
  &[type='number'] {
    -moz-appearance: textfield;
  }

  &[type='number']::-webkit-outer-spin-button,
  &[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Adornment = styled.div<{ position: 'start' | 'end' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) =>
    props.position === 'start' ? 'left: 1.25rem;' : 'right: 1.25rem;'}
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: 1rem;
  pointer-events: none;
  z-index: 1;

  /* Mobile adjustments */
  @media (max-width: 767px) {
    ${(props) => (props.position === 'start' ? 'left: 1rem;' : 'right: 1rem;')}
    font-size: 0.875rem;
  }
`;

// ErrorText and HelperText are now handled by FieldWrapper

// ============================================================================
// Component Interface Extensions
// ============================================================================

interface ExtendedFormFieldProps
  extends Omit<FormFieldProps, 'onChange' | 'onBlur' | 'onFocus'> {
  // React Hook Form integration
  register?: any; // UseFormRegister<any>
  registerOptions?: RegisterOptions;
  error?: FieldError | string | undefined;

  // Enhanced event handlers
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  // Additional HTML input props
  min?: number | string;
  max?: number | string;
  step?: number | string;
  pattern?: string;
  maxLength?: number;
  minLength?: number;
  list?: string;

  // Accessibility enhancements
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: boolean;
  role?: string;
}

// ============================================================================
// FormField Component
// ============================================================================

export const FormField = forwardRef<HTMLInputElement, ExtendedFormFieldProps>(
  (
    {
      label,
      name,
      type = 'text',
      placeholder,
      required = false,
      disabled = false,
      readOnly = false,
      autoComplete,
      autoFocus = false,
      value,
      defaultValue,
      error,
      helperText,
      startAdornment,
      endAdornment,
      register,
      registerOptions,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      className,
      id,
      testId,
      'data-testid': dataTestId,
      min,
      max,
      step,
      pattern,
      maxLength,
      minLength,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid,
      role,
      ...rest
    },
    ref,
  ) => {
    // Generate unique IDs using utility
    const ids = useFormIds(name, id);

    // Determine error message
    const errorMessage = typeof error === 'string' ? error : error?.message;
    const hasError = !!errorMessage;

    // Build aria-describedby using utility
    const describedByIds = buildAriaDescribedBy([
      ariaDescribedBy,
      hasError ? ids.errorId : null,
      helperText && !hasError ? ids.helperId : null,
    ]);

    // Register field once with react-hook-form (fix for double registration)
    const registration = register
      ? register(name, {
          required: required ? `${label} es requerido` : false,
          ...registerOptions,
        })
      : null;

    // Merge refs properly to avoid conflicts
    const mergedRef = mergeRefs(ref, registration?.ref);

    // Input props
    const inputProps = {
      id: ids.inputId,
      name,
      type,
      placeholder,
      disabled,
      readOnly,
      autoComplete,
      autoFocus,
      value,
      defaultValue,
      min,
      max,
      step,
      pattern,
      maxLength,
      minLength,
      'aria-invalid': ariaInvalid ?? hasError,
      'aria-describedby': describedByIds,
      'aria-labelledby': ariaLabelledBy,
      'aria-required': required,
      role,
      'data-testid': dataTestId || testId || `form-field-${name}`,
      ref: mergedRef,
      ...rest,
    };

    return (
      <FieldWrapper
        id={ids.inputId}
        label={label}
        required={required}
        disabled={disabled}
        error={errorMessage}
        helperText={helperText}
        className={className}
      >
        <InputContainer>
          {startAdornment && (
            <Adornment position="start">{startAdornment}</Adornment>
          )}

          <Input
            {...inputProps}
            hasError={hasError}
            hasStartAdornment={!!startAdornment}
            hasEndAdornment={!!endAdornment}
            onChange={(e) => {
              // Call registration onChange first, then custom onChange
              registration?.onChange?.(e);
              onChange?.(e);
            }}
            onBlur={(e) => {
              // Call registration onBlur first, then custom onBlur
              registration?.onBlur?.(e);
              onBlur?.(e);
            }}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
          />

          {endAdornment && <Adornment position="end">{endAdornment}</Adornment>}
        </InputContainer>
      </FieldWrapper>
    );
  },
);

FormField.displayName = 'FormField';

// ============================================================================
// Default Export
// ============================================================================

export default FormField;
